import { ReactNode } from "react"
import { Navigate, Outlet, useSearchParams } from "react-router-dom"

import UnauthorizedAccess from "pages/Error/401"
import usePermissions, { PERMISSIONS } from "./usePermissions"

type TPermissionKey = keyof typeof PERMISSIONS

type TQueryPermissionRule = {
    /** Query param to inspect, e.g. "t" for main tabs or "st" for sub tabs */
    param: string
    /** Param value → permission(s) required for that value. Values not in the map are allowed. */
    map: Partial<Record<string, TPermissionKey | TPermissionKey[]>>
}

type TPermissionRouteProps = {
    /** Single permission key, or a list — access is granted if ANY of them is true */
    permission: TPermissionKey | TPermissionKey[]
    /** Extra checks driven by query params (e.g. ?t=ticket-details requiring a tab permission) */
    queryPermissions?: TQueryPermissionRule[]
    /** When provided, redirect here instead of rendering the 401 page */
    fallbackRoute?: string
    children?: ReactNode
}

const PermissionRoute = ({ permission, queryPermissions, fallbackRoute, children }: TPermissionRouteProps) => {
    const permissions = usePermissions()
    const [searchParams] = useSearchParams()

    const isAllowed = (required: TPermissionKey | TPermissionKey[]) => {
        const keys = Array.isArray(required) ? required : [required]
        return keys.some(key => permissions[key])
    }

    const hasAccess =
        isAllowed(permission) &&
        (queryPermissions ?? []).every(rule => {
            const value = searchParams.get(rule.param)
            const required = value !== null ? rule.map[value] : undefined
            return required === undefined || isAllowed(required)
        })

    if (!hasAccess) {
        return fallbackRoute ? <Navigate to={fallbackRoute} replace /> : <UnauthorizedAccess />
    }

    return children ? <>{children}</> : <Outlet />
}

export default PermissionRoute
